import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Plane,
  Ship,
  Truck,
  Warehouse,
  Shield,
  Users,
  FileCheck,
  Clock,
  Globe,
  Package,
  CheckCircle,
  ArrowRight,
  Zap,
  Target,
  Award,
  DollarSign,
  Navigation,
  ChevronRight,
  Phone,
  MessageCircle,
  Star
} from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{count}{suffix}</span>;
};

const ServicesPage = () => {
  const { language } = useLanguage();
  const [statsVisible, setStatsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const statsRef = useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    pt: {
      hero: {
        badge: 'NOSSOS SERVIÇOS',
        title: 'Soluções Completas em',
        titleHighlight: 'Logística Internacional',
        subtitle: 'Do planejamento à entrega, oferecemos soluções personalizadas para cada etapa da sua operação de comércio exterior.',
      },
      mainServices: [
        {
          id: 'aereo',
          icon: Plane,
          title: 'Transporte Aéreo',
          tagline: 'Por que escolher o Transporte Aéreo?',
          description: 'A Well Cargo é parceira das principais companhias aéreas do mercado, oferecendo soluções ágeis e seguras para importações e exportações com embarques para os principais aeroportos internacionais do mundo.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          colorAccent: 'purple',
          highlights: [
            { icon: Zap, text: 'Rapidez' },
            { icon: Shield, text: 'Segurança' },
            { icon: Globe, text: 'Alcance global' },
          ],
          features: ['Maior previsibilidade', 'Cargas sensíveis', 'Alto valor agregado', 'Perecíveis e farmacêuticos'],
          details: [
            { title: 'Rapidez', desc: 'É o modal mais rápido do comércio internacional, ideal para reduzir lead time, atender prazos urgentes e manter a continuidade da cadeia produtiva.' },
            { title: 'Maior previsibilidade', desc: 'Voos regulares e menor exposição a atrasos climáticos ou operacionais garantem mais confiabilidade nos prazos de entrega.' },
            { title: 'Segurança da carga', desc: 'Menor tempo de trânsito reduz riscos de avarias, extravios e roubos, sendo indicado para cargas de alto valor agregado.' },
            { title: 'Ideal para cargas sensíveis', desc: 'Perfeito para mercadorias perecíveis, farmacêuticas, eletrônicos, amostras, peças urgentes e produtos com prazo de validade curto.' },
            { title: 'Alcance global', desc: 'Conecta rapidamente os principais mercados do mundo, com ampla malha aérea internacional.' },
          ],
        },
        {
          id: 'maritimo',
          icon: Ship,
          title: 'Transporte Marítimo',
          tagline: 'Economia e capacidade para grandes volumes',
          description: 'A melhor relação custo-benefício para cargas de grande volume. Operamos FCL, LCL, Break Bulk e cargas projeto com os principais armadores.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          colorAccent: 'cyan',
          highlights: [
            { icon: Package, text: '24Mil' },
            { icon: Globe, text: '95+ países' },
            { icon: DollarSign, text: 'Até 40% economia' },
          ],
          features: ['FCL & LCL', 'Break Bulk', 'Cargas Projeto', 'Ro-Ro'],
        },
        {
          id: 'rodoviario',
          icon: Truck,
          title: 'Transporte Rodoviário',
          tagline: 'Flexibilidade e cobertura nacional',
          description: 'Conexão eficiente entre portos, aeroportos e destinos finais. Cobertura completa no Brasil e Mercosul com rastreamento GPS.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          colorAccent: 'emerald',
          highlights: [
            { icon: Zap, text: 'Coleta em 24h' },
            { icon: Navigation, text: 'Rastreamento GPS' },
            { icon: Target, text: 'Porta a porta' },
          ],
          features: ['Carga completa', 'Fracionado', 'DTA/MIC-DTA', 'Cargas especiais'],
        },
      ],
      otherServices: {
        title: 'Serviços',
        titleHighlight: 'Complementares',
        subtitle: 'Soluções integradas para uma operação completa de comércio exterior.',
        items: [
          {
            icon: Warehouse,
            title: 'Armazenagem',
            description: 'Rede global de warehouses para armazenagem temporária ou de longo prazo, com serviços de etiquetagem, repacking e consolidação.',
          },
          {
            icon: FileCheck,
            title: 'Desembaraço Aduaneiro',
            description: 'Estrutura especializada em regulamentações aduaneiras, garantindo operações com eficácia, conformidade e agilidade.',
          },
          {
            icon: Shield,
            title: 'Seguro Internacional',
            description: 'Seguros com cobertura ampla e personalizada para proteção total da sua carga durante todo o trajeto internacional.',
          },
          {
            icon: Users,
            title: 'Consultoria',
            description: 'Assessoria especializada em logística internacional, auxiliando na otimização de processos e redução de custos.',
          },
        ],
      },
      stats: {
        title: 'Por que escolher a',
        titleHighlight: 'Well Cargo?',
        items: [
          { value: 15, suffix: '+', label: 'Anos de experiência' },
          { value: 95, suffix: '+', label: 'Países atendidos' },
          { value: 500, suffix: '+', label: 'Clientes ativos' },
          { value: 98, suffix: '%', label: 'Satisfação' },
        ],
      },
      differentials: {
        title: 'Nossos',
        titleHighlight: 'Diferenciais',
        items: [
          { icon: Users, title: 'Atendimento Consultivo', desc: 'Equipe dedicada que entende seu negócio' },
          { icon: Target, title: 'Foco em Resultados', desc: 'Processos otimizados para eficiência' },
          { icon: Clock, title: 'Agilidade', desc: 'Respostas rápidas e soluções ágeis' },
          { icon: Globe, title: 'Rede Global', desc: 'Parceiros em 196 países via WCA World' },
          { icon: Shield, title: 'Segurança', desc: 'Carga protegida em todas as etapas' },
          { icon: Award, title: 'Expertise', desc: '15+ anos de mercado internacional' },
        ],
      },
      cta: {
        title: 'Não sabe qual solução escolher?',
        subtitle: 'Nossa equipe está pronta para analisar sua operação e recomendar a melhor opção.',
        button: 'Falar com especialista',
        phone: '+55 27 99758-0648',
      },
      buttons: {
        learnMore: 'Saiba mais',
        requestQuote: 'Solicitar cotação',
      },
    },
    en: {
      hero: {
        badge: 'OUR SERVICES',
        title: 'Complete Solutions in',
        titleHighlight: 'International Logistics',
        subtitle: 'From planning to delivery, we offer customized solutions for each stage of your foreign trade operation.',
      },
      mainServices: [
        {
          id: 'aereo',
          icon: Plane,
          title: 'Air Transport',
          tagline: 'Why choose Air Transport?',
          description: 'Well Cargo is a partner of the main airlines in the market, offering agile and secure solutions for imports and exports with shipments to the main international airports in the world.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          colorAccent: 'purple',
          highlights: [
            { icon: Zap, text: 'Speed' },
            { icon: Shield, text: 'Security' },
            { icon: Globe, text: 'Global reach' },
          ],
          features: ['Greater predictability', 'Sensitive cargo', 'High added value', 'Perishables & pharmaceuticals'],
          details: [
            { title: 'Speed', desc: 'It is the fastest mode in international trade, ideal for reducing lead time, meeting urgent deadlines and maintaining supply chain continuity.' },
            { title: 'Greater predictability', desc: 'Regular flights and less exposure to weather or operational delays ensure more reliability in delivery times.' },
            { title: 'Cargo security', desc: 'Shorter transit time reduces risks of damage, loss and theft, being indicated for high added value cargo.' },
            { title: 'Ideal for sensitive cargo', desc: 'Perfect for perishable goods, pharmaceuticals, electronics, samples, urgent parts and products with short shelf life.' },
            { title: 'Global reach', desc: 'Quickly connects the main markets in the world, with extensive international air network.' },
          ],
        },
        {
          id: 'maritimo',
          icon: Ship,
          title: 'Sea Transport',
          tagline: 'Economy and capacity for large volumes',
          description: 'The best cost-benefit ratio for large volume cargo. We operate FCL, LCL, Break Bulk and project cargo with major carriers.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          colorAccent: 'cyan',
          highlights: [
            { icon: Package, text: '24K' },
            { icon: Globe, text: '95+ countries' },
            { icon: DollarSign, text: 'Up to 40% savings' },
          ],
          features: ['FCL & LCL', 'Break Bulk', 'Project Cargo', 'Ro-Ro'],
        },
        {
          id: 'rodoviario',
          icon: Truck,
          title: 'Road Transport',
          tagline: 'Flexibility and national coverage',
          description: 'Efficient connection between ports, airports and final destinations. Complete coverage in Brazil and Mercosur with GPS tracking.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          colorAccent: 'emerald',
          highlights: [
            { icon: Zap, text: 'Pickup in 24h' },
            { icon: Navigation, text: 'Real-time GPS' },
            { icon: Target, text: 'Door to door' },
          ],
          features: ['Full truckload', 'LTL', 'DTA/MIC-DTA', 'Special cargo'],
        },
      ],
      otherServices: {
        title: 'Complementary',
        titleHighlight: 'Services',
        subtitle: 'Integrated solutions for a complete foreign trade operation.',
        items: [
          {
            icon: Warehouse,
            title: 'Warehousing',
            description: 'Global network of warehouses for temporary or long-term storage, with labeling, repacking and consolidation services.',
          },
          {
            icon: FileCheck,
            title: 'Customs Clearance',
            description: 'Specialized structure in customs regulations, ensuring operations with effectiveness, compliance and agility.',
          },
          {
            icon: Shield,
            title: 'International Insurance',
            description: 'Insurance with broad and personalized coverage for total protection of your cargo throughout the international journey.',
          },
          {
            icon: Users,
            title: 'Consulting',
            description: 'Specialized consulting in international logistics, helping to optimize processes and reduce costs.',
          },
        ],
      },
      stats: {
        title: 'Why choose',
        titleHighlight: 'Well Cargo?',
        items: [
          { value: 15, suffix: '+', label: 'Years of experience' },
          { value: 95, suffix: '+', label: 'Countries served' },
          { value: 500, suffix: '+', label: 'Active clients' },
          { value: 98, suffix: '%', label: 'Satisfaction' },
        ],
      },
      differentials: {
        title: 'Our',
        titleHighlight: 'Differentials',
        items: [
          { icon: Users, title: 'Consultative Service', desc: 'Dedicated team that understands your business' },
          { icon: Target, title: 'Results Focus', desc: 'Optimized processes for efficiency' },
          { icon: Clock, title: 'Agility', desc: 'Quick responses and agile solutions' },
          { icon: Globe, title: 'Global Network', desc: 'Partners in 196 countries via WCA World' },
          { icon: Shield, title: 'Security', desc: 'Cargo protected at all stages' },
          { icon: Award, title: 'Expertise', desc: '15+ years in international market' },
        ],
      },
      cta: {
        title: "Don't know which solution to choose?",
        subtitle: 'Our team is ready to analyze your operation and recommend the best option.',
        button: 'Talk to specialist',
        phone: '+55 27 99758-0648',
      },
      buttons: {
        learnMore: 'Learn more',
        requestQuote: 'Request quote',
      },
    },
    es: {
      hero: {
        badge: 'NUESTROS SERVICIOS',
        title: 'Soluciones Completas en',
        titleHighlight: 'Logística Internacional',
        subtitle: 'De la planificación a la entrega, ofrecemos soluciones personalizadas para cada etapa de su operación de comercio exterior.',
      },
      mainServices: [
        {
          id: 'aereo',
          icon: Plane,
          title: 'Transporte Aéreo',
          tagline: '¿Por qué elegir el Transporte Aéreo?',
          description: 'Well Cargo es socio de las principales aerolíneas del mercado, ofreciendo soluciones ágiles y seguras para importaciones y exportaciones con embarques a los principales aeropuertos internacionales del mundo.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          colorAccent: 'purple',
          highlights: [
            { icon: Zap, text: 'Rapidez' },
            { icon: Shield, text: 'Seguridad' },
            { icon: Globe, text: 'Alcance global' },
          ],
          features: ['Mayor previsibilidad', 'Cargas sensibles', 'Alto valor agregado', 'Perecederos y farmacéuticos'],
          details: [
            { title: 'Rapidez', desc: 'Es el modal más rápido del comercio internacional, ideal para reducir lead time, atender plazos urgentes y mantener la continuidad de la cadena productiva.' },
            { title: 'Mayor previsibilidad', desc: 'Vuelos regulares y menor exposición a retrasos climáticos u operacionales garantizan más confiabilidad en los plazos de entrega.' },
            { title: 'Seguridad de la carga', desc: 'Menor tiempo de tránsito reduce riesgos de averías, extravíos y robos, siendo indicado para cargas de alto valor agregado.' },
            { title: 'Ideal para cargas sensibles', desc: 'Perfecto para mercancías perecederas, farmacéuticas, electrónicos, muestras, piezas urgentes y productos con plazo de validez corto.' },
            { title: 'Alcance global', desc: 'Conecta rápidamente los principales mercados del mundo, con amplia malla aérea internacional.' },
          ],
        },
        {
          id: 'maritimo',
          icon: Ship,
          title: 'Transporte Marítimo',
          tagline: 'Economía y capacidad para grandes volúmenes',
          description: 'La mejor relación costo-beneficio para cargas de gran volumen. Operamos FCL, LCL, Break Bulk y cargas proyecto con los principales armadores.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          colorAccent: 'cyan',
          highlights: [
            { icon: Package, text: '24Mil' },
            { icon: Globe, text: '95+ países' },
            { icon: DollarSign, text: 'Hasta 40% ahorro' },
          ],
          features: ['FCL & LCL', 'Break Bulk', 'Carga Proyecto', 'Ro-Ro'],
        },
        {
          id: 'rodoviario',
          icon: Truck,
          title: 'Transporte Terrestre',
          tagline: 'Flexibilidad y cobertura nacional',
          description: 'Conexión eficiente entre puertos, aeropuertos y destinos finales. Cobertura completa en Brasil y Mercosur con rastreo GPS.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          colorAccent: 'emerald',
          highlights: [
            { icon: Zap, text: 'Recogida en 24h' },
            { icon: Navigation, text: 'GPS tiempo real' },
            { icon: Target, text: 'Puerta a puerta' },
          ],
          features: ['Carga completa', 'Fraccionado', 'DTA/MIC-DTA', 'Cargas especiales'],
        },
      ],
      otherServices: {
        title: 'Servicios',
        titleHighlight: 'Complementarios',
        subtitle: 'Soluciones integradas para una operación completa de comercio exterior.',
        items: [
          {
            icon: Warehouse,
            title: 'Almacenamiento',
            description: 'Red global de almacenes para almacenamiento temporal o de largo plazo, con servicios de etiquetado, repacking y consolidación.',
          },
          {
            icon: FileCheck,
            title: 'Despacho Aduanero',
            description: 'Estructura especializada en regulaciones aduaneras, garantizando operaciones con eficacia, conformidad y agilidad.',
          },
          {
            icon: Shield,
            title: 'Seguro Internacional',
            description: 'Seguros con cobertura amplia y personalizada para protección total de su carga durante todo el trayecto internacional.',
          },
          {
            icon: Users,
            title: 'Consultoría',
            description: 'Asesoría especializada en logística internacional, ayudando a optimizar procesos y reducir costos.',
          },
        ],
      },
      stats: {
        title: '¿Por qué elegir',
        titleHighlight: 'Well Cargo?',
        items: [
          { value: 15, suffix: '+', label: 'Años de experiencia' },
          { value: 95, suffix: '+', label: 'Países atendidos' },
          { value: 500, suffix: '+', label: 'Clientes activos' },
          { value: 98, suffix: '%', label: 'Satisfacción' },
        ],
      },
      differentials: {
        title: 'Nuestros',
        titleHighlight: 'Diferenciales',
        items: [
          { icon: Users, title: 'Atención Consultiva', desc: 'Equipo dedicado que entiende su negocio' },
          { icon: Target, title: 'Enfoque en Resultados', desc: 'Procesos optimizados para eficiencia' },
          { icon: Clock, title: 'Agilidad', desc: 'Respuestas rápidas y soluciones ágiles' },
          { icon: Globe, title: 'Red Global', desc: 'Socios en 196 países via WCA World' },
          { icon: Shield, title: 'Seguridad', desc: 'Carga protegida en todas las etapas' },
          { icon: Award, title: 'Experiencia', desc: '15+ años en mercado internacional' },
        ],
      },
      cta: {
        title: '¿No sabe qué solución elegir?',
        subtitle: 'Nuestro equipo está listo para analizar su operación y recomendar la mejor opción.',
        button: 'Hablar con especialista',
        phone: '+55 27 99758-0648',
      },
      buttons: {
        learnMore: 'Saber más',
        requestQuote: 'Solicitar cotización',
      },
    },
  };

  const c = content[language];

  // Intersection observer for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a6b5c] via-[#0f4a3f] to-[#0a332b]" />
        
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Floating icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Plane className="absolute top-1/4 right-[15%] w-12 h-12 text-white/10 animate-float" style={{ animationDelay: '0s' }} />
          <Ship className="absolute top-1/2 right-[25%] w-16 h-16 text-white/10 animate-float" style={{ animationDelay: '1s' }} />
          <Truck className="absolute bottom-1/4 right-[10%] w-14 h-14 text-white/10 animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/10 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6 animate-fade-in">
            {c.hero.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {c.hero.title}<br />
            <span className="text-[#3ECDA3] italic">{c.hero.titleHighlight}</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.4s' }}>
            {c.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {c.mainServices.map((service, index) => {
              const IconComponent = service.icon;
              const isEven = index % 2 === 1;
              const isHovered = hoveredService === service.id;

              return (
                <div
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${isEven ? 'lg:flex-row-reverse' : ''}`}
                  onMouseEnter={() => setHoveredService(service.id)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  {/* Image */}
                  <div className={`relative group ${isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className={`w-full h-[300px] md:h-[400px] object-cover transition-transform duration-700 ${isHovered ? 'scale-105' : 'scale-100'}`}
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-70 mix-blend-multiply`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Icon badge */}
                      <div className="absolute top-6 left-6">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                      </div>

                      {/* Title overlay */}
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-3xl md:text-4xl font-bold text-white">{service.title}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${isEven ? 'lg:order-1' : ''}`}>
                    <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-8">
                      {service.description}
                    </p>

                    {/* Highlights */}
                    <div className="grid grid-cols-3 gap-4 mb-8">
                      {service.highlights.map((highlight, i) => {
                        const HighlightIcon = highlight.icon;
                        return (
                          <div key={i} className="text-center p-4 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                            <HighlightIcon className={`w-6 h-6 mx-auto mb-2 ${
                              service.colorAccent === 'purple' ? 'text-purple-500' :
                              service.colorAccent === 'cyan' ? 'text-cyan-500' : 'text-emerald-500'
                            }`} />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{highlight.text}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {service.features.map((feature, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full ${
                            service.colorAccent === 'purple' ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' :
                            service.colorAccent === 'cyan' ? 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' : 
                            'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to={service.link}
                        className={`group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                          service.colorAccent === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-purple-500/30' :
                          service.colorAccent === 'cyan' ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg hover:shadow-cyan-500/30' : 
                          'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg hover:shadow-emerald-500/30'
                        }`}
                      >
                        {c.buttons.learnMore}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                      <Link
                        to="/contato"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-[#3ECDA3] hover:text-[#3ECDA3] transition-all duration-300"
                      >
                        {c.buttons.requestQuote}
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-gradient-to-r from-[#1a6b5c] to-[#0f4a3f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {c.stats.title} <span className="text-[#3ECDA3] italic">{c.stats.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={statsVisible} />
                </div>
                <p className="text-[#3ECDA3] text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {c.otherServices.title} <span className="text-[#3ECDA3] italic">{c.otherServices.titleHighlight}</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto">
              {c.otherServices.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {c.otherServices.items.map((service, index) => {
              const IconComp = service.icon;
              return (
                <div
                  key={index}
                  className="group bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:bg-[#3ECDA3]/5 dark:hover:bg-[#3ECDA3]/10 border border-transparent hover:border-[#3ECDA3]/20 transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-14 h-14 bg-[#3ECDA3]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#3ECDA3] transition-colors duration-300">
                    <IconComp className="w-7 h-7 text-[#3ECDA3] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{service.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.differentials.title} <span className="text-[#3ECDA3] italic">{c.differentials.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {c.differentials.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 mx-auto bg-white dark:bg-gray-900 rounded-2xl shadow-lg flex items-center justify-center mb-4 group-hover:bg-[#3ECDA3] transition-colors duration-300">
                    <IconComp className="w-8 h-8 text-[#3ECDA3] group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="font-bold text-gray-900 dark:text-white mb-1 text-sm">{item.title}</h4>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a6b5c] to-[#0f4a3f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {c.cta.title}
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            {c.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contato"
              className="group inline-flex items-center justify-center gap-2 bg-white text-[#1a6b5c] px-8 py-4 rounded-xl font-semibold hover:bg-[#3ECDA3] hover:text-white transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              {c.cta.button}
            </Link>
            <a
              href={`tel:${c.cta.phone}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              {c.cta.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
